/**
 * InstallsTodayCard (TODAY-V3) — the install desk's slice of the morning:
 * the derived work queues (needs scheduling / needs permit / ready to close)
 * with the actual jobs, one click from here to the job workspace. Counts are
 * live state, so there's nothing to clear — a job leaves the list the moment
 * someone does the thing.
 */
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, ShieldCheck, CheckCircle2, ArrowRight, Zap } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { useJobs, useJobQueues, type Job } from "@/features/jobs/jobs";
import { useSubInvoices } from "@/features/jobs/subinvoices";

const QUEUES = [
  { key: "needsSchedule" as const, label: "Needs Scheduling", icon: CalendarClock, cls: "bg-amber-50 text-amber-800", who: "install coordinator" },
  { key: "needsPermit" as const, label: "Needs Permit", icon: ShieldCheck, cls: "bg-sky-50 text-sky-700", who: "permit desk" },
  { key: "readyToClose" as const, label: "Ready to Close", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700", who: "install coordinator" },
];

export function InstallsTodayCard() {
  const { data: jobs } = useJobs();
  const { data: queues } = useJobQueues(jobs);
  const { data: subInvoices } = useSubInvoices("review");
  const navigate = useNavigate();
  if (!queues) return null;
  const byId = new Map((jobs ?? []).map((j) => [j.id, j]));

  const rows: { job: Job; queue: (typeof QUEUES)[number] }[] = [];
  for (const q of QUEUES) {
    for (const id of queues[q.key]) {
      const job = byId.get(id);
      if (job) rows.push({ job, queue: q });
    }
  }
  const nInvoices = subInvoices?.length ?? 0;
  if (rows.length === 0 && nInvoices === 0) return null;
  const shown = rows.slice(0, 6);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2">
        {nInvoices > 0 && (
          <Link
            to="/jobs"
            title="Powerhouse invoices waiting for review on the Jobs page"
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 transition-opacity hover:opacity-80"
          >
            <Zap className="h-3.5 w-3.5" /> Sub invoices
            <span className="tabular-nums">{nInvoices}</span>
          </Link>
        )}
        {QUEUES.map((q) => {
          const n = queues[q.key].size;
          if (n === 0) return null;
          return (
            <Link
              key={q.key}
              to={`/jobs?queue=${q.key}`}
              title={`Open the ${q.who}'s queue on the Jobs page`}
              className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-opacity hover:opacity-80", q.cls)}
            >
              <q.icon className="h-3.5 w-3.5" /> {q.label}
              <span className="tabular-nums">{n}</span>
              {/* PERMIT-01: the crew asked — nobody has pulled these yet. */}
              {q.key === "needsPermit" && queues.permitRequested.size > 0 && (
                <span className="rounded-full bg-amber-500 px-1.5 text-[10.5px] font-bold text-white">{queues.permitRequested.size} requested</span>
              )}
            </Link>
          );
        })}
        <Link to="/jobs" className="ml-auto inline-flex items-center gap-1 text-[13px] font-medium text-brand-700 hover:text-brand-900">
          All jobs <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <ul className="divide-y divide-slate-100">
        {shown.map(({ job, queue }) => (
          <li
            key={`${queue.key}:${job.id}`}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/jobs/${job.id}`)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/jobs/${job.id}`); } }}
            className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors hover:bg-brand-50/40"
          >
            <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold", queue.cls)}>{queue.label}</span>
            <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
              <span className="font-semibold text-slate-800">{job.customer_name ?? job.custno}</span>
              {job.title ? <span className="text-slate-500"> — {job.title}</span> : null}
            </span>
            {job.address && <span className="hidden truncate text-xs text-slate-400 sm:block">{job.address}</span>}
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
          </li>
        ))}
      </ul>
      {rows.length > shown.length && (
        <Link to="/jobs" className="block border-t border-slate-100 px-4 py-2 text-center text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50/40">
          {rows.length - shown.length} more on the Jobs page
        </Link>
      )}
    </Card>
  );
}
